import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchStudentCourse } from '../services/api';
import {
  AssessmentAnswerPayload,
  AssessmentForm,
  AssessmentLinkedQuestion,
  AssessmentMeasurement,
  AssessmentSpecificQuestion,
  assessmentApi,
} from '../services/assessmentApi';
import { CheckCircleIcon, InfoIcon } from '../utils/icons';
import { getLocalizedText } from '../utils/i18n';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import { getFirstModulePath } from '../utils/navigation';
import { normalizeId } from '../utils/id';
import { useProgressContext } from '../components/ModulePageLayout';
import MarkdownRenderer from '../components/MarkdownRenderer';
import '../styles/components/_assessment.scss';

type AssessmentState =
  | 'loading'
  | 'intro'
  | 'in-progress'
  | 'submitting'
  | 'submitted'
  | 'already-done'
  | 'error';

type Question = (AssessmentLinkedQuestion | AssessmentSpecificQuestion) & {
  /** True si c'est une question liée. Le backend nous passe `linkedKey` dans ce cas. */
  __isLinked: boolean;
};

const buildQuestionList = (form: AssessmentForm): Question[] => {
  const linked = (form.linkedQuestions || []).map((q) => ({ ...q, __isLinked: true }));
  const specific = (form.specificQuestions || []).map((q) => ({ ...q, __isLinked: false }));
  return [...linked, ...specific];
};

const getMeasurementLabel = (
  measurement: AssessmentMeasurement,
  form: AssessmentForm | null,
  t: any
): string => {
  if (measurement === 'initial') return t('assessment.diagnostic.label');
  if (measurement === 'final') return t('assessment.final.label');
  if (form?.label) return getLocalizedText(form.label as any) || t('assessment.followup.label');
  return t('assessment.followup.label');
};

const getIntroText = (
  measurement: AssessmentMeasurement,
  form: AssessmentForm | null,
  t: any
): { title: string; body: string } => {
  if (measurement === 'initial') {
    return {
      title: t('assessment.diagnostic.introTitle'),
      body:
        (form?.description ? getLocalizedText(form.description as any) : '') ||
        t('assessment.diagnostic.introBody'),
    };
  }
  if (measurement === 'final') {
    return {
      title: t('assessment.final.introTitle'),
      body:
        (form?.description ? getLocalizedText(form.description as any) : '') ||
        t('assessment.final.introBody'),
    };
  }
  return {
    title:
      (form?.label ? getLocalizedText(form.label as any) : '') ||
      t('assessment.followup.introTitle'),
    body:
      (form?.description ? getLocalizedText(form.description as any) : '') ||
      t('assessment.followup.introBody'),
  };
};

// Stocke la réponse en cours par identifiant stable (linkedKey ou _id).
const keyOfQuestion = (q: Question): string =>
  q.__isLinked ? `lk:${(q as AssessmentLinkedQuestion).linkedKey}` : `id:${q._id}`;

interface AnswerValue {
  selectedOptionIndex?: number;
  scaleValue?: number;
  textValue?: string;
}

const Assessment: React.FC = () => {
  const { courseId, measurement: measurementParam, followupId } = useParams<{
    courseId: string;
    measurement?: AssessmentMeasurement;
    followupId?: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshCourse } = useProgressContext();

  // Si la route contient un followupId, on est forcément en suivi (route /assessment/followup/:id).
  const measurement: AssessmentMeasurement = followupId
    ? 'followup'
    : (measurementParam || 'initial');

  const [state, setState] = useState<AssessmentState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AssessmentForm | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [navigatingToFirstModule, setNavigatingToFirstModule] = useState(false);
  const loadingRef = useRef(false);

  const questions = useMemo(() => (form ? buildQuestionList(form) : []), [form]);
  const currentQuestion: Question | null = questions[currentIndex] || null;
  const total = questions.length;
  const isLastQuestion = total > 0 && currentIndex === total - 1;

  useEffect(() => {
    if (loadingRef.current) return;
    if (!courseId || !measurement) return;
    loadingRef.current = true;
    void load().finally(() => {
      loadingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, measurement, followupId]);

  const load = async () => {
    try {
      setError(null);
      setState('loading');
      if ((measurement === 'final' || measurement === 'followup') && courseId) {
        const overview = await assessmentApi.getCourseAssessments(courseId);
        if (overview.enabled && measurement === 'final') {
          if (
            !overview.initial?.submitted &&
            overview.eligibility.finalImpactBlockedReason === 'diagnostic'
          ) {
            navigate(`/course/${courseId}/assessment/initial`, { replace: true });
            return;
          }
        }
        if (overview.enabled && measurement === 'followup' && followupId) {
          const fu = overview.followups?.find(
            (f) => normalizeId(f.id) === normalizeId(followupId)
          );
          if (fu?.pendingDiagnostic) {
            navigate(`/course/${courseId}/assessment/initial`, { replace: true });
            return;
          }
        }
      }
      const data = await assessmentApi.getAssessment(courseId!, measurement, followupId);
      setForm(data);
      setState('intro');
    } catch (err: any) {
      console.error('Error loading assessment:', err);
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 409) {
          setState('already-done');
          return;
        }
        if (status === 403) {
          setState('error');
          setError(getLearnerFacingApiError(err, t));
          return;
        }
      }
      setError(getLearnerFacingApiError(err, t));
      setState('error');
    }
  };

  /** Vérifie si la question courante a une réponse valide. */
  const hasValidAnswer = (q: Question | null): boolean => {
    if (!q) return false;
    const v = answers[keyOfQuestion(q)];
    if (!v) return false;
    if (q.type === 'weighted-mcq') {
      return typeof v.selectedOptionIndex === 'number' && v.selectedOptionIndex >= 0;
    }
    if (q.type === 'scale') {
      return typeof v.scaleValue === 'number';
    }
    if (q.type === 'open-text') {
      return typeof v.textValue === 'string' && v.textValue.trim().length > 0;
    }
    return false;
  };

  const updateAnswer = (q: Question, patch: AnswerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [keyOfQuestion(q)]: { ...prev[keyOfQuestion(q)], ...patch },
    }));
    setError(null);
  };

  const handleStart = () => {
    setCurrentIndex(0);
    setState('in-progress');
  };

  const handlePrevious = () => {
    setError(null);
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (!currentQuestion) return;
    if (!hasValidAnswer(currentQuestion)) {
      setError(t('assessment.missingAnswerError'));
      return;
    }
    setError(null);
    if (!isLastQuestion) {
      setCurrentIndex(currentIndex + 1);
    } else {
      void handleSubmit();
    }
  };

  const buildPayload = (): AssessmentAnswerPayload[] => {
    const out: AssessmentAnswerPayload[] = [];
    for (const q of questions) {
      const v = answers[keyOfQuestion(q)];
      if (!v) continue;
      if (q.__isLinked) {
        const lk = (q as AssessmentLinkedQuestion).linkedKey;
        if (q.type === 'weighted-mcq') {
          out.push({ linkedKey: lk, selectedOptionIndex: v.selectedOptionIndex! });
        } else if (q.type === 'scale') {
          out.push({ linkedKey: lk, scaleValue: v.scaleValue! });
        }
      } else {
        const id = q._id;
        if (q.type === 'weighted-mcq') {
          out.push({ questionId: id, selectedOptionIndex: v.selectedOptionIndex! });
        } else if (q.type === 'scale') {
          out.push({ questionId: id, scaleValue: v.scaleValue! });
        } else if (q.type === 'open-text') {
          out.push({ questionId: id, textValue: (v.textValue || '').trim() });
        }
      }
    }
    return out;
  };

  const handleSubmit = async () => {
    // Vérifie la couverture de toutes les questions avant l'appel API.
    const missing = questions.find((q) => !hasValidAnswer(q));
    if (missing) {
      const idx = questions.indexOf(missing);
      setCurrentIndex(idx);
      setError(t('assessment.missingAnswerError'));
      return;
    }

    setSubmitting(true);
    setState('submitting');
    setError(null);
    try {
      await assessmentApi.submitAssessment(
        courseId!,
        measurement,
        buildPayload(),
        followupId
      );
      try {
        await refreshCourse();
      } catch (refreshErr) {
        console.warn('refreshCourse after assessment submit:', refreshErr);
      }
      setState('submitted');
    } catch (err: any) {
      console.error('Error submitting assessment:', err);
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setState('already-done');
        return;
      }
      setError(getLearnerFacingApiError(err, t));
      setState('in-progress');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToCourse = () => navigate(`/course/${courseId}`);
  /** Après le diagnostic : navigation directe vers le premier module (sans passer par la page formation). */
  const handleStartCourse = async () => {
    if (!courseId) return;
    try {
      setNavigatingToFirstModule(true);
      await refreshCourse();
      const courseData = await fetchStudentCourse(courseId, { full: true });
      const path = getFirstModulePath(courseId, courseData);
      if (path) {
        navigate(path);
      } else {
        navigate(`/course/${courseId}`);
      }
    } catch (err) {
      console.error('Error resolving first module:', err);
      navigate(`/course/${courseId}`);
    } finally {
      setNavigatingToFirstModule(false);
    }
  };

  // -------- Rendu --------

  if (state === 'loading') {
    return (
        <Box className="assessment__loading">
          <CircularProgress color="inherit" />
        </Box>
    );
  }

  if (state === 'error') {
    return (
        <Box className="assessment">
          <Card className="assessment__card">
            <CardContent className="assessment__card-content assessment__card-content--centered">
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || t('assessment.loadingError')}
              </Alert>
              <Button
                variant="contained"
                className="button-primary assessment__button"
                onClick={handleBackToCourse}
              >
                {t('assessment.backToCourseButton')}
              </Button>
            </CardContent>
          </Card>
        </Box>
    );
  }

  if (state === 'already-done') {
    return (
        <Box className="assessment">
          <Card className="assessment__card">
            <CardContent className="assessment__card-content assessment__card-content--centered">
              <Box className="assessment__success-icon">
                <CheckCircleIcon size={72} color="#00d377" />
              </Box>
              <p className="assessment__success-title">{t('assessment.alreadySubmitted')}</p>
              <p className="assessment__success-text">{t('assessment.alreadySubmittedMessage')}</p>
              <Box className="assessment__actions assessment__actions--centered">
                <Button
                  variant="contained"
                  className="button-primary assessment__button"
                  onClick={handleBackToCourse}
                >
                  {t('assessment.backToCourseButton')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
    );
  }

  if (state === 'submitted') {
    const thanksMessage =
      measurement === 'initial'
        ? t('assessment.thanksDiagnostic')
        : measurement === 'final'
        ? t('assessment.thanksFinal')
        : t('assessment.thanksFollowup');

    return (
        <Box className="assessment">
          <Card className="assessment__card">
            <CardContent className="assessment__card-content assessment__card-content--centered">
              <Box className="assessment__success-icon">
                <CheckCircleIcon size={80} color="#00d377" />
              </Box>
              <p className="assessment__success-title">{t('assessment.thanksTitle')}</p>
              <p className="assessment__success-text">{thanksMessage}</p>
              <Box className="assessment__actions assessment__actions--centered">
                <Button
                  variant="contained"
                  className="button-primary assessment__button"
                  onClick={
                    measurement === 'initial' ? handleStartCourse : handleBackToCourse
                  }
                  disabled={measurement === 'initial' && navigatingToFirstModule}
                >
                  {measurement === 'initial'
                    ? navigatingToFirstModule
                      ? t('common.loading')
                      : t('assessment.continueToCourseButton')
                    : measurement === 'final'
                    ? t('assessment.backToCourseButton')
                    : t('assessment.backToCourseButton')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
    );
  }

  // Intro
  if (state === 'intro') {
    const intro = getIntroText(measurement, form, t);
    const startButton =
      measurement === 'initial'
        ? t('assessment.diagnostic.startButton')
        : measurement === 'final'
        ? t('assessment.final.startButton')
        : t('assessment.followup.startButton');

    return (
        <Box className="assessment">
          <Card className="assessment__card">
            <CardContent className="assessment__card-content">
              <Box className="assessment__card-header">
                <p className="assessment__intro-label">
                  {getMeasurementLabel(measurement, form, t)}
                </p>
                <p className="assessment__intro-title">{intro.title}</p>
                <Box
                  className="assessment__intro-body assessment__intro-body--rich"
                  sx={{ mt: 0, '& .MuiBox-root': { width: '100%' } }}
                >
                  <MarkdownRenderer
                    content={intro.body}
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  />
                </Box>
              </Box>

              <Box className="assessment__intro-info">
                <InfoIcon size={20} color="#13322B" />
                <Typography variant="body2" className="assessment__intro-info-text">
                  {total} {t('assessment.questionLabel').toLowerCase()}
                  {total > 1 ? 's' : ''}
                </Typography>
              </Box>

              <Box className="assessment__actions assessment__actions--centered">
                <Button
                  variant="contained"
                  className="button-primary assessment__button"
                  onClick={handleStart}
                  disabled={total === 0}
                >
                  {startButton}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
    );
  }

  // Submitting (transition spinner)
  if (state === 'submitting') {
    return (
        <Box className="assessment__loading">
          <CircularProgress color="inherit" />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('assessment.submitting')}
          </Typography>
        </Box>
    );
  }

  // In-progress : show currentQuestion
  if (!currentQuestion) {
    return null;
  }

  const currentValue = answers[keyOfQuestion(currentQuestion)] || {};

  return (
      <Box className="assessment">
        <Card className="assessment__question-card">
          <CardContent className="assessment__card-content">
            <Box className="assessment__card-header">
              <p className="assessment__question-header">
                {t('assessment.questionLabel')} {currentIndex + 1} {t('assessment.of')} {total}
              </p>
              <p className="assessment__question-text">
                {getLocalizedText(currentQuestion.question as any)}
              </p>
              <p className="assessment__question-instructions">
                {currentQuestion.type === 'weighted-mcq'
                  ? t('assessment.selectAnswer')
                  : currentQuestion.type === 'scale'
                  ? t('assessment.selectScale')
                  : t('assessment.writeAnswer')}
              </p>
            </Box>

            {error && (
              <Alert severity="warning" className="assessment__error-alert">
                {error}
              </Alert>
            )}

            <Box className="assessment__question-body">
              {currentQuestion.type === 'weighted-mcq' && (
                <RadioGroup
                  value={
                    typeof currentValue.selectedOptionIndex === 'number'
                      ? currentValue.selectedOptionIndex.toString()
                      : ''
                  }
                  onChange={(e) =>
                    updateAnswer(currentQuestion, {
                      selectedOptionIndex: parseInt(e.target.value, 10),
                    })
                  }
                  className="assessment__options"
                >
                  {(currentQuestion.options || []).map((option, idx) => {
                    const isSelected = currentValue.selectedOptionIndex === idx;
                    return (
                      <FormControlLabel
                        key={idx}
                        value={idx.toString()}
                        control={<Radio />}
                        label={getLocalizedText(option.text as any)}
                        className={`assessment__option ${
                          isSelected ? 'assessment__option--selected' : ''
                        }`}
                      />
                    );
                  })}
                </RadioGroup>
              )}

              {currentQuestion.type === 'scale' && currentQuestion.scale && (
                <Box className="assessment__scale">
                  <Box className="assessment__scale-buttons">
                    {(() => {
                      const { min, max, step } = currentQuestion.scale;
                      const stepValue = step && step > 0 ? step : 1;
                      const items: number[] = [];
                      for (let v = min; v <= max; v += stepValue) items.push(v);
                      return items.map((v) => {
                        const isSelected = currentValue.scaleValue === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            className={`assessment__scale-button ${
                              isSelected ? 'assessment__scale-button--selected' : ''
                            }`}
                            onClick={() =>
                              updateAnswer(currentQuestion, { scaleValue: v })
                            }
                          >
                            {v}
                          </button>
                        );
                      });
                    })()}
                  </Box>
                  {(currentQuestion.scale.minLabel || currentQuestion.scale.maxLabel) && (
                    <Box className="assessment__scale-labels">
                      <span>
                        {currentQuestion.scale.minLabel
                          ? getLocalizedText(currentQuestion.scale.minLabel as any)
                          : currentQuestion.scale.min}
                      </span>
                      <span>
                        {currentQuestion.scale.maxLabel
                          ? getLocalizedText(currentQuestion.scale.maxLabel as any)
                          : currentQuestion.scale.max}
                      </span>
                    </Box>
                  )}
                </Box>
              )}

              {currentQuestion.type === 'open-text' && (
                <Box className="assessment__open-text">
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    inputProps={{ maxLength: 5000 }}
                    placeholder={t('assessment.openTextPlaceholder')}
                    value={currentValue.textValue || ''}
                    onChange={(e) =>
                      updateAnswer(currentQuestion, { textValue: e.target.value })
                    }
                  />
                  <Typography variant="caption" className="assessment__open-text-hint">
                    {t('assessment.openTextHint')} ({(currentValue.textValue || '').length} / 5000)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box className="assessment__actions">
              <Button
                variant="text"
                onClick={handlePrevious}
                disabled={currentIndex === 0 || submitting}
                className="assessment__button-secondary"
              >
                {t('assessment.previousButton')}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={submitting}
                className="button-primary assessment__button"
              >
                {submitting
                  ? t('assessment.submitting')
                  : isLastQuestion
                  ? t('assessment.completeButton')
                  : t('assessment.nextButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
  );
};

export default Assessment;
